// /compare/vsco-workspace: Pixally vs VSCO Workspace (formerly Táve).
//
// Read the header of honeybook.js first: every rule there applies here. VSCO's
// facts, with sources and exact words: docs/geo/compare-gallery-platforms-evidence.md.
// VSCO's pages do not describe film delivery, second shooters or post-production,
// so the table says "not described", never "no".

export const ROUTE = '/compare/vsco-workspace';
export const UPDATED_AT = '2026-10-05';
export const CHECKED_AT = 'October 2, 2026';
export const CHECKED_LABEL = 'VSCO facts checked';
export const HERO_ART = 'versus';

export const TITLE = 'Pixally vs VSCO Workspace (formerly Táve)';
export const SEO_TITLE = 'Pixally vs VSCO Workspace (Táve) for Wedding Photographers';
export const SEO_DESCRIPTION =
  'How Pixally and VSCO Workspace, formerly Táve, compare on price, galleries, film delivery, second shooters and post-production, with every VSCO fact sourced.';

export const LEDE =
  "VSCO Workspace is Táve's photography CRM, bought by VSCO in 2025 and now sold only inside VSCO One, an annual membership that also includes client galleries and VSCO's editing tools. Pixally is a CRM built for wedding photographers and videographers, launching on January 1, 2027, with film delivery, second shooters and post-production tracking built in.";

const VSCO_WORKSPACE = 'https://www.vsco.co/workspace';
const VSCO_PLANS = 'https://www.vsco.co/subscribe/plans';

export const BODY = [
  {
    type: 'paragraph',
    text: "If you are a photographer who wants deep automation, galleries and editing in one membership today, VSCO One is a strong choice. If you deliver films or run a crew, the differences below matter.",
  },
  { type: 'heading', level: 2, text: 'Side by side' },
  {
    type: 'paragraph',
    text: `The VSCO column comes from VSCO's own Workspace and plans pages, checked ${CHECKED_AT}.`,
  },
  {
    type: 'table',
    label: 'Pixally and VSCO Workspace compared',
    head: ['', 'Pixally', 'VSCO Workspace'],
    rows: [
      ['Available', 'From January 1, 2027', 'Now'],
      ['Price', 'Free plan; Basic $33, Pro $58, Studio $99 a month billed yearly, or monthly', 'VSCO One: $499.99 a year (about $41.67 a month), annual only'],
      ['Free plan or trial', 'A 14-day free trial with no card, then the Free plan: up to 5 active booked projects', '30 days risk free'],
      ['Client galleries', 'Photo, video and hybrid galleries built in, on paid plans', 'Unlimited client galleries in VSCO One'],
      ['Film delivery', 'Built in: 50 GB to start on paid plans, then packs; no limit on film length', "Not described on VSCO's Workspace or plans pages"],
      ['Editing tools', 'No', "VSCO's editing tools included in VSCO One"],
      ['Text messaging', 'SMS reminders on Pro and Studio', 'Two-way texting (US and Canada)'],
      ['Second shooters and contractors', 'Free contractor accounts; second shooter management and a contractor portal on Pro and Studio', "Not described on VSCO's Workspace page"],
      ['Post-production tracking', 'On Pro and Studio', "Not described on VSCO's Workspace page"],
      ['Moving your data in', 'Free migration of your last 12 months of active projects', 'Free onboarding and CRM migration with tailored setup and training'],
    ],
  },
  { type: 'heading', level: 2, text: 'When VSCO Workspace is the better choice' },
  {
    type: 'list',
    items: [
      'It exists today. Pixally launches on January 1, 2027.',
      'Galleries, editing and a CRM in one membership: VSCO One includes all three for $499.99 a year.',
      'Two-way text messaging with clients, in the US and Canada.',
      'VSCO describes Workspace as offering more automation, customization and reporting power than most photography CRMs.',
      'Free onboarding, with migration, tailored setup and one-on-one training.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you deliver films, run second shooters and editors, or would rather pay monthly or start on a free plan.',
  },
  { type: 'heading', level: 3, text: 'Film delivery' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: "VSCO's Workspace and plans pages describe client galleries but not film delivery. Pixally puts no limit on film length, plays films with chapters so a couple can jump to the ceremony or the first dance, and sells delivery in packs (50 GB for $50, 100 GB for $95, 500 GB for $450) that never expire.",
  },
  { type: 'heading', level: 3, text: 'Second shooters and contractors' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: 'Second shooters get a [free contractor account](/contractor-terms) that works across every studio they shoot for, with their availability, travel fee and tax form in one place. Second shooter management and the contractor portal are on Pro (up to 10) and Studio (unlimited).',
  },
  { type: 'heading', level: 3, text: 'Post-production' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: "Pixally's post-production management (Pro and Studio) follows the stages of a wedding edit: uploads, culling and selects, edits assigned to your team or outside editors with due dates, review, client revisions and delivery.",
  },
  { type: 'heading', level: 3, text: 'How you pay' },
  {
    type: 'paragraph',
    text: 'VSCO One is annual only, at $499.99. Pixally has a free plan, and Basic, Pro and Studio can be paid monthly ($39, $69, $119) or yearly ($33, $58, $99 a month).',
  },
  { type: 'heading', level: 2, text: 'Switching from VSCO Workspace or Táve' },
  { type: 'art', name: 'switching' },
  {
    type: 'paragraph',
    text: 'Pixally will move your last 12 months of active client projects across for free, and turn your existing documents into Pixally templates. Winter, when the season is quiet, is the easiest time to switch.',
  },
];

export const FAQ = [
  {
    q: 'Did Táve become VSCO Workspace?',
    a: "Yes. VSCO acquired Táve's studio manager in May 2025 and launched it as VSCO Workspace in August 2025.",
  },
  {
    q: 'How much does VSCO Workspace cost?',
    a: 'It comes in the VSCO One membership, at $499.99 a year (about $41.67 a month), billed annually only, with 30 days risk free. VSCO One also includes unlimited client galleries and VSCO editing tools.',
  },
  {
    q: 'Can I move from VSCO Workspace or Táve to Pixally?',
    a: 'Yes. Pixally moves your last 12 months of active projects across for free, on every plan.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'VSCO Workspace', url: VSCO_WORKSPACE },
  { label: 'VSCO plans (VSCO One)', url: VSCO_PLANS },
  { label: 'Pixally plans and video packs: the Pixally pricing table', url: null },
  { label: 'Pixally contractor accounts: Contractor Terms of Use, Section 5.1', url: '/contractor-terms' },
];
